// Modified from: https://github.com/ueberdosis/tiptap/blob/main/packages/extension-link/src/helpers/autolink.ts
// This is needed because the default behaviour aggressively unlinks items while typing,
// producing weird behaviour
//
// Modifications from the original:
// 1. No longer removes links at all
// 2. Only adds link after typing a space at the end
import {
	combineTransactionSteps,
	findChildrenInRange,
	getChangedRanges,
} from '@tiptap/core';
import { find } from 'linkifyjs';
import { MarkType } from 'prosemirror-model';
import { Plugin, PluginKey } from 'prosemirror-state';
import { getLinkId } from './util';

type AutolinkOptions = {
	type: MarkType;
	validate?: (url: string) => boolean;
}

export function autolink(options: AutolinkOptions): Plugin {
	return new Plugin({
		key: new PluginKey('trAutolink'),
		appendTransaction: (transactions, oldState, newState) => {
			const docChanges = transactions.some((transaction) => transaction.docChanged)
				&& !oldState.doc.eq(newState.doc);
			const preventAutolink = transactions.some((transaction) => transaction.getMeta('preventAutolink'));

			if (!docChanges || preventAutolink) {
				return null;
			}

			const { tr } = newState;
			const transform = combineTransactionSteps(oldState.doc, [...transactions]);
			const changes = getChangedRanges(transform);

			changes.forEach(({ newRange }) => {
				// now let’s see if we can add new links
				findChildrenInRange(newState.doc, newRange, (node) => node.isTextblock)
					.forEach((textBlock) => {
						// we need to define a placeholder for leaf nodes
						// so that the link position can be calculated correctly
						const text = newState.doc.textBetween(
							textBlock.pos,
							textBlock.pos + textBlock.node.nodeSize,
							undefined,
							' ',
						);

						find(text)
							.filter((link) => link.isLink)
							.filter((link) => {
								if (options.validate) {
									return options.validate(link.value);
								}

								return true;
							})
							// calculate link position
							.map((link) => ({
								...link,
								from: textBlock.pos + link.start + 1,
								to: textBlock.pos + link.end + 1,
							}))
							// Ignore links that do not have a whitespace at the end
							.filter((link) => {
								const followingCharacter = text[link.end];
								return followingCharacter && followingCharacter.match(/\s/);
							})
							// check if link is within the changed range
							.filter((link) => {
								const fromIsInRange = newRange.from >= link.from && newRange.from <= link.to;
								const toIsInRange = newRange.to >= link.from && newRange.to <= link.to;

								return fromIsInRange || toIsInRange;
							})
							// add link mark
							.forEach((link) => {
								tr.addMark(link.from, link.to, options.type.create({
									href: link.href,
									id: getLinkId(),
								}));
							});
					});
			});

			if (!tr.steps.length) {
				return null;
			}

			return tr;
		},
	});
}
